import { RelicNameValues, relicSettingsOverrideLookup } from './relics/relic';
import { WorldNameValues } from './worlds/world';

export interface RunSettings {
  [RunSettingsHandler.keys.StartWorldId]: WorldNameValues;
  [RunSettingsHandler.keys.InstantRestart]: boolean;
  [RunSettingsHandler.keys.RegenOnRestart]: boolean;
  [RunSettingsHandler.keys.ShouldSpawnChunksForCollectedRelics]: boolean;
  [RunSettingsHandler.keys.ActiveRelics]: RelicNameValues[];
  [RunSettingsHandler.keys.MaxAirJumps]: number;
  [RunSettingsHandler.keys.GlideTime]: number;
  [RunSettingsHandler.keys.StartingContinueTokens]: number;
  [RunSettingsHandler.keys.CanBreakAllSmashables]: boolean; //?
  [RunSettingsHandler.keys.MagnetStrength]: number;
  [RunSettingsHandler.keys.StartingGlobalSpeed]: number;
  [RunSettingsHandler.keys.MaxGlobalSpeed]: number;
}

export interface RunSettingsActive extends RunSettings {
  [RunSettingsHandler.keys.ContinueTokens]: number;
  [RunSettingsHandler.keys.GlobalSpeed]: number;
  [RunSettingsHandler.keys.WorldsCompleted]: number;
  [RunSettingsHandler.keys.RushesCompleted]: number;
}

export class RunSettingsHandler {
  public static keys = {
    StartWorldId: 'startWorldId',
    InstantRestart: 'instantRestart',
    RegenOnRestart: 'regenOnRestart',
    ShouldSpawnChunksForCollectedRelics: 'shouldSpawnChunksForCollectedRelics',
    CanBreakAllSmashables: 'canBreakAllSmashables',
    ShouldRelicsActivateOnPickup: 'shouldRelicsActivateOnPickup',
    ActiveRelics: 'activeRelics',
    MaxAirJumps: 'maxAirJumps',
    GlideTime: 'glideTime',
    MagnetStrength: 'magnetStrength',
    StartingContinueTokens: 'startingContinueTokens',
    StartingGlobalSpeed: 'startingGlobalSpeed',
    MaxGlobalSpeed: 'maxGlobalSpeed',

    ContinueTokens: 'continueTokens',
    GlobalSpeed: 'globalSpeed',
    WorldsCompleted: 'worldsCompleted',
    RushesCompleted: 'rushesCompleted'
  } as const;

  public static events = {
    ChangeMaxAirJumps: `changedata-${RunSettingsHandler.keys.MaxAirJumps}`,
    ChangeGlobalSpeed: `changedata-${RunSettingsHandler.keys.GlobalSpeed}`
  } as const;

  protected scene: Phaser.Scene;
  protected runSettings: RunSettings;

  constructor(scene: Phaser.Scene, runSettings: RunSettings) {
    this.scene = scene;
    this.runSettings = runSettings;

    this.resetData();
  }

  public resetData() {
    let settings: RunSettingsActive = Object.assign({}, this.runSettings, {
      continueTokens: this.runSettings.startingContinueTokens,
      globalSpeed: this.runSettings.startingGlobalSpeed,
      worldsCompleted: 0,
      rushesCompleted: 0
    });

    this.activateRelics(settings);
    this.scene.registry.set(settings);
  }

  public static getMaxJumps(scene: Phaser.Scene): number {
    return scene.registry.get(RunSettingsHandler.keys.MaxAirJumps);
  }

  public static getGlideTime(scene: Phaser.Scene): number {
    return scene.registry.get(RunSettingsHandler.keys.GlideTime);
  }

  public static getMagnetStrength(scene: Phaser.Scene): number {
    return scene.registry.get(RunSettingsHandler.keys.MagnetStrength);
  }

  public static canBreakAllSmashables(scene: Phaser.Scene): boolean {
    return scene.registry.get(RunSettingsHandler.keys.CanBreakAllSmashables);
  }

  public static shouldSpawnChunksForCollectedRelics(scene: Phaser.Scene): boolean {
    return scene.registry.get(RunSettingsHandler.keys.ShouldSpawnChunksForCollectedRelics);
  }

  public static onMaxJumpsChange(scene: Phaser.Scene, callback: (parent: any, value: any, previousValue: any) => void, context?: any) {
    scene.registry.events.on(RunSettingsHandler.events.ChangeMaxAirJumps, callback, context);
  }

  public static onGlobalSpeedChange(scene: Phaser.Scene, callback: (parent: any, value: any, previousValue: any) => void, context?: any) {
    scene.registry.events.on(RunSettingsHandler.events.ChangeGlobalSpeed, callback, context);
  }

  public static isRelicActive(scene: Phaser.Scene, relicName: RelicNameValues): boolean {
    return scene.registry.get(RunSettingsHandler.keys.ActiveRelics).includes(relicName);
  }

  public static canContinueWithToken(scene: Phaser.Scene) {
    let tokens = scene.registry.get(RunSettingsHandler.keys.ContinueTokens);

    if (tokens > 0) {
      scene.registry.set(RunSettingsHandler.keys.ContinueTokens, tokens - 1);
      return true;
    } else {
      return false;
    }
  }

  public static worldComplete(scene: Phaser.Scene) {
    scene.registry.inc(RunSettingsHandler.keys.WorldsCompleted, 1);
  }

  public static rushComplete(scene: Phaser.Scene) {
    scene.registry.inc(RunSettingsHandler.keys.RushesCompleted, 1);
  }

  public static getCompletedRushes(scene: Phaser.Scene) {
    return scene.registry.get(RunSettingsHandler.keys.RushesCompleted);
  }

  public static getGlobalSpeed(scene: Phaser.Scene) {
    return scene.registry.get(RunSettingsHandler.keys.GlobalSpeed);
  }

  public static getMaxGlobalSpeed(scene: Phaser.Scene) {
    return scene.registry.get(RunSettingsHandler.keys.MaxGlobalSpeed);
  }

  public static increaseGlobalSpeed(scene: Phaser.Scene, speedIncrease: number): number {
    let globalSpeed = RunSettingsHandler.getGlobalSpeed(scene);
    let maxGlobalSpeed = RunSettingsHandler.getMaxGlobalSpeed(scene);

    if (globalSpeed + speedIncrease < maxGlobalSpeed) {
      scene.registry.inc(RunSettingsHandler.keys.GlobalSpeed, speedIncrease);
      return globalSpeed + speedIncrease;
    } else if (globalSpeed < maxGlobalSpeed) {
      scene.registry.set(RunSettingsHandler.keys.GlobalSpeed, maxGlobalSpeed);
      return maxGlobalSpeed;
    }

    return globalSpeed;
  }

  public static decreaseGlobalSpeed(scene: Phaser.Scene, speedDecrease: number): number {
    let globalSpeed = RunSettingsHandler.getGlobalSpeed(scene);
    let minGlobalSpeed = 1;

    if (globalSpeed - speedDecrease <= minGlobalSpeed) {
      scene.registry.set(RunSettingsHandler.keys.GlobalSpeed, minGlobalSpeed);
      return minGlobalSpeed;
    } else if (globalSpeed - speedDecrease > minGlobalSpeed) {
      scene.registry.set(RunSettingsHandler.keys.GlobalSpeed, globalSpeed - speedDecrease);
      return globalSpeed - speedDecrease;
    }

    return globalSpeed;
  }

  public static activateRelic(scene: Phaser.Scene, relicName: RelicNameValues) {
    if (!scene.registry.get(RunSettingsHandler.keys.ShouldRelicsActivateOnPickup)) return;

    // TODO: Update settings with relics override.
  }

  protected activateRelics(runSettings: RunSettingsActive) {
    runSettings.activeRelics.forEach((relicName) => {
      if (relicSettingsOverrideLookup[relicName]) {
        relicSettingsOverrideLookup[relicName].override(runSettings);
      }
    });
  }
}
